/**
 * reCAPTCHA v3 クライアントヘルパー
 *
 * VITE_RECAPTCHA_V3_SITE_KEY が未設定の場合は no-op（null トークンを返す）。
 * サーバ側 (`RECAPTCHA_V3_SECRET_KEY`) も同様にフェイルオープン設計。
 */

// reCAPTCHA v3 サイトキーは公開情報（HTML に出力される）なので直接埋め込み。
// 環境変数で上書き可能。
const SITE_KEY =
  (import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY as string | undefined) ||
  "6LfIBugsAAAAACdLZxDCVuZSMQFmeWjEe5Wrw_Fw";

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}

let loadingPromise: Promise<void> | null = null;

export function isRecaptchaEnabled(): boolean {
  return Boolean(SITE_KEY);
}

export function loadRecaptcha(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (!SITE_KEY) return Promise.resolve();
  if (window.grecaptcha) return Promise.resolve();
  if (loadingPromise) return loadingPromise;

  loadingPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-recaptcha-v3="1"]',
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("recaptcha load failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    s.async = true;
    s.defer = true;
    s.dataset.recaptchaV3 = "1";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("recaptcha load failed"));
    document.head.appendChild(s);
  });
  return loadingPromise;
}

/**
 * 指定 action の reCAPTCHA v3 トークンを取得。
 * 未設定 / 失敗時は null を返し、呼び出し元でそのまま送信を継続できる。
 */
export async function getRecaptchaToken(action: string): Promise<string | null> {
  if (!SITE_KEY || typeof window === "undefined") return null;
  try {
    await loadRecaptcha();
    if (!window.grecaptcha) return null;
    return await new Promise<string | null>((resolve) => {
      window.grecaptcha!.ready(() => {
        window
          .grecaptcha!.execute(SITE_KEY, { action })
          .then((token) => resolve(token))
          .catch(() => resolve(null));
      });
    });
  } catch (e) {
    console.warn("[recaptcha] token fetch failed:", e);
    return null;
  }
}
